import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config.js';
import EnhancedVideoPlayer from './EnhancedVideoPlayerV2.js';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import { useConfirm } from './ui/useConfirm.js';
import Button from './ui/Button.js';
import { LoadingScreen } from './ui/Spinner.js';
import PracticePlanModal from './PracticePlanModal.jsx';
import './TeamCalendar.css';

function TeamCalendar() {
  const { user } = useAuth();
  const toast = useToast();
  const [confirm, confirmModal] = useConfirm();
  const [events, setEvents] = useState([]);
  const [classSchedules, setClassSchedules] = useState([]);
  const [competitionEvents, setCompetitionEvents] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('month'); // 'month', 'week', 'list'
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classExceptions, setClassExceptions] = useState([]);

  // Meet detail modal
  const [showMeetDetailModal, setShowMeetDetailModal] = useState(false);
  const [selectedMeetDetail, setSelectedMeetDetail] = useState(null);
  const [meetChecklist, setMeetChecklist] = useState([]);
  const [newChecklistItem, setNewChecklistItem] = useState('');

  // Bulk create modal
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [deleteConfirmEvent, setDeleteConfirmEvent] = useState(null); // Teams-style delete scope
  const [selectedEventCard, setSelectedEventCard] = useState(null); // event detail card
  const [editingOccurrence, setEditingOccurrence] = useState(null); // single-practice edit (retime/move)
  const [practicePlan, setPracticePlan] = useState(null); // { classId, calendarEventId, date, className }
  const [planSummary, setPlanSummary] = useState({}); // "class_id|date" -> { theme, goals, slots } for calendar overlay
  const [bulkDeleteFilter, setBulkDeleteFilter] = useState({ title: '', start_date: '', end_date: '' });
  const [bulkDeleteMatches, setBulkDeleteMatches] = useState([]);
  const [bulkDeleteSelected, setBulkDeleteSelected] = useState([]);
  const [bulkDeleteSearched, setBulkDeleteSearched] = useState(false);
  const [bulkEvent, setBulkEvent] = useState({
    title: '', event_type: 'practice', location_id: '', location_details: '',
    description: '', start_date: '', end_date: '',
    start_time: '09:00', end_time: '10:00', days_of_week: [],
    assignTo: 'all',      // 'all' | 'class' | 'athlete'
    assignClassId: '',
    assignAthleteIds: []
  });

  // Unified assignment state for new events
  const [allAthletes, setAllAthletes] = useState([]);
  const [allClasses, setAllClasses] = useState([]);
  const [videoSummary, setVideoSummary] = useState({}); // date-string → {count, athlete_names}
  const [athleteVideos, setAthleteVideos] = useState({}); // date-string → video[] (athlete role only)

  // Class filter sidebar + day panel
  const [selectedClassFilter, setSelectedClassFilter] = useState(null);
  const [clickedDay, setClickedDay] = useState(null);

  // Practice rotation planner

  // New event form state
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    event_type: 'practice',
    location_id: '',
    location_details: '',
    start_datetime: '',
    end_datetime: '',
    is_recurring: false,
    recurrence_rule: '',
    recurrence_end_date: '',
    assignTo: 'all',      // 'all' | 'class' | 'athlete'
    assignClassId: '',
    assignAthleteIds: [],  // Changed to array for multi-select
    repeatDates: []        // additional YYYY-MM-DD days to also place this event on
  });
  const [repeatDateInput, setRepeatDateInput] = useState('');

  useEffect(() => {
    fetchEvents();
    fetchPlanSummary();
    fetchLocations();
    fetchCompetitionEvents();
    fetchClassExceptions();
    fetchVideoSummary();
    fetchAllAthletes();
    fetchAllClasses();
    if (user?.id) {
      fetchClassSchedules(user);
    }
  }, [selectedDate]);

  const fetchClassSchedules = async (userData) => {
    try {
      const token = userData.token;

      if (userData.role === 'athlete') {
        // The login user id is NOT the athlete record id — resolve the athlete
        // record first, then pull its schedule (otherwise practices never show).
        const headers = { 'Authorization': `Bearer ${token}` };
        let athleteId = userData.athlete_id || userData.athleteId;
        if (!athleteId) {
          try {
            const me = await axios.get(`${API_BASE_URL}/api/athlete/me`, { headers });
            athleteId = me.data?.id || me.data?.athlete_id;
          } catch { /* not linked yet */ }
        }
        if (!athleteId) { setClassSchedules([]); return; }
        const response = await axios.get(
          `${API_BASE_URL}/api/athlete/${athleteId}/weekly-schedule`, { headers });
        setClassSchedules(Array.isArray(response.data) ? response.data : []);
      } else if (userData.role === 'parent') {
        // Fetch all children and combine their schedules
        const childrenRes = await axios.get(
          `${API_BASE_URL}/api/parent/${userData.id}/children`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        const children = Array.isArray(childrenRes.data) ? childrenRes.data : [];
        const allSchedules = [];
        await Promise.all(children.map(async (child) => {
          const childId = child.athlete_id || child.id;
          const childName = child.athlete_name || child.name;
          if (!childId) return;
          const schedRes = await axios.get(
            `${API_BASE_URL}/api/athlete/${childId}/weekly-schedule`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          const schedules = Array.isArray(schedRes.data) ? schedRes.data : [];
          schedules.forEach(s => allSchedules.push({ ...s, athlete_name: childName }));
        }));
        setClassSchedules(allSchedules);
      } else {
        // coaches/admins: combine schedules across ALL team classes so the
        // recurring practice times set in Class Management appear on the calendar.
        const headers = { 'Authorization': `Bearer ${token}` };
        const classesRes = await axios.get(`${API_BASE_URL}/api/classes`, { headers });
        const classes = Array.isArray(classesRes.data) ? classesRes.data : [];
        const allSchedules = [];
        await Promise.all(classes.map(async (cls) => {
          try {
            const r = await axios.get(`${API_BASE_URL}/api/classes/${cls.id}/schedule`, { headers });
            (Array.isArray(r.data) ? r.data : []).forEach(s =>
              allSchedules.push({ ...s, class_name: cls.class_name, level: cls.level })
            );
          } catch (_) { /* skip a class whose schedule fetch fails */ }
        }));
        setClassSchedules(allSchedules);
      }
    } catch (err) {
      console.error('Error fetching class schedules:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
      
      const response = await axios.get(`${API_BASE_URL}/api/calendar/events`, {
        params: {
          start_date: startOfMonth.toISOString(),
          end_date: endOfMonth.toISOString()
        }
      });
      const fetchedEvents = Array.isArray(response.data) ? response.data : [];
      console.log('📅 TeamCalendar: Fetched calendar events:', fetchedEvents.length);
      fetchedEvents.forEach(e => {
        console.log(`  - "${e.title}" (${e.event_type}) @ ${e.start_datetime} - Target:`, e.target_groups);
      });
      setEvents(fetchedEvents);
    } catch (err) {
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  // local YYYY-MM-DD (matches how practice plans are keyed on class_id + date)
  const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Pull the practice-plan overlay for the visible month so tiles can show the
  // plan's theme + built rotation times (plan → calendar continuity).
  const fetchPlanSummary = async () => {
    if (!user || (user.role !== 'admin' && user.role !== 'coach')) return;
    try {
      const start = ymd(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));
      const end = ymd(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0));
      const res = await axios.get(`${API_BASE_URL}/api/v1/practice-plans/summary`, {
        params: { start, end }
      });
      const map = {};
      (Array.isArray(res.data) ? res.data : []).forEach(p => {
        if (p.class_id != null) map[`c${p.class_id}|${p.practice_date}`] = p;
        if (p.calendar_event_id != null) map[`e${p.calendar_event_id}|${p.practice_date}`] = p;
      });
      setPlanSummary(map);
    } catch (err) {
      // non-fatal: calendar still renders without the plan overlay
    }
  };

  const fetchCompetitionEvents = async () => {
    try {
      const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);

      const response = await axios.get(`${API_BASE_URL}/api/competition-events`, {
        params: {
          start_date: startOfMonth.toISOString().split('T')[0],
          end_date: endOfMonth.toISOString().split('T')[0]
        }
      });
      setCompetitionEvents(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching competition events:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/locations`);
      setLocations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setLocations([]);
    }
  };

  const fetchClassExceptions = async () => {
    try {
      const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
      const response = await axios.get(`${API_BASE_URL}/api/class-schedule-exceptions`, {
        params: {
          start_date: startOfMonth.toISOString().split('T')[0],
          end_date: endOfMonth.toISOString().split('T')[0]
        }
      });
      setClassExceptions(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching class exceptions:', err);
    }
  };

  const fetchVideoSummary = async () => {
    try {
      const y = selectedDate.getFullYear();
      const m = selectedDate.getMonth() + 1;
      if (user?.role === 'athlete' && user?.id) {
        // Fetch athlete's own videos, group by date
        const response = await axios.get(
          `${API_BASE_URL}/athletes/${user.id}/videos`,
          { params: { year: y, month: m }, headers: { Authorization: `Bearer ${user.token}` } }
        );
        const videos = Array.isArray(response.data) ? response.data : [];
        const summaryMap = {};
        const videoMap = {};
        videos.forEach(v => {
          const date = new Date(v.created_at).toISOString().split('T')[0];
          if (!summaryMap[date]) { summaryMap[date] = { count: 0 }; videoMap[date] = []; }
          summaryMap[date].count++;
          videoMap[date].push(v);
        });
        setVideoSummary(summaryMap);
        setAthleteVideos(videoMap);
      } else {
        // Admin/Coach: fetch team-wide video summary
        const response = await axios.get(`${API_BASE_URL}/api/team-videos/summary`, {
          params: { year: y, month: m }
        });
        const map = {};
        (Array.isArray(response.data) ? response.data : []).forEach(r => {
          map[r.date] = { count: r.count, athlete_names: r.athlete_names };
        });
        setVideoSummary(map);
      }
    } catch (err) {
      // silently ignore access errors
    }
  };

  const fetchAllAthletes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/athletes`);
      setAllAthletes(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching athletes:', err);
    }
  };

  const fetchAllClasses = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/classes`);
      setAllClasses(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching classes:', err);
    }
  };

  const handleCancelClassDate = async (schedule, date) => {
    const dateStr = date.toISOString().split('T')[0];
    const reason = window.prompt(`Reason for cancelling ${schedule.class_name} on ${dateStr}:`, 'No class');
    if (reason === null) return; // cancelled prompt
    try {
      await axios.post(`${API_BASE_URL}/api/class-schedule-exceptions`, {
        class_schedule_id: schedule.id,
        exception_date: dateStr,
        reason: reason || 'No class'
      });
      fetchClassExceptions();
    } catch (err) {
      toast.error('Failed to cancel class date');
    }
  };

  const handleRestoreClassDate = async (exceptionId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/class-schedule-exceptions/${exceptionId}`);
      fetchClassExceptions();
    } catch (err) {
      toast.error('Failed to restore class date');
    }
  };

  // Edit a single practice occurrence: change its time and/or move it to another
  // date. Writes one override on class_schedule_exceptions (keyed by the original
  // Open the layered practice plan (rotation board, workout, S&C, theme) for a
  // practice occurrence. Anchors on the class + the rendered date.
  const openPracticePlan = (event, dateObj) => {
    const d = dateObj ? new Date(dateObj) : new Date(event.start_datetime);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setPracticePlan({
      classId: event.scheduleData?.class_id || null,
      calendarEventId: event.isClassSchedule ? null : (event.id || null),
      date: dateStr,
      className: event.title
    });
  };

  // Find the saved practice plan (theme + rotation times) for an event occurrence,
  // matched on class_id (recurring practices) or calendar_event_id + the rendered date.
  const planForEvent = (event, date) => {
    const ds = ymd(date);
    const cid = event.scheduleData?.class_id;
    if (cid != null && planSummary[`c${cid}|${ds}`]) return planSummary[`c${cid}|${ds}`];
    if (event.id != null && planSummary[`e${event.id}|${ds}`]) return planSummary[`e${event.id}|${ds}`];
    return null;
  };

  // recurring date), leaving the rest of the series intact.
  const openEditOccurrence = (event) => {
    const s = event.scheduleData;
    if (!s) return;
    const renderedDate = (event.start_datetime || '').slice(0, 10);
    setEditingOccurrence({
      schedule: s,
      occurrenceDate: event.exceptionDate || renderedDate, // exception_date key
      exceptionId: event.exceptionId || null,
      newDate: renderedDate,
      newStart: (event.start_datetime || '').slice(11, 16) || String(s.start_time || '').slice(0, 5),
      newEnd: (event.end_datetime || '').slice(11, 16) || String(s.end_time || '').slice(0, 5),
    });
  };

  const handleSaveOccurrence = async () => {
    const o = editingOccurrence;
    if (!o) return;
    try {
      await axios.post(`${API_BASE_URL}/api/class-schedule-exceptions`, {
        class_schedule_id: o.schedule.id,
        exception_date: o.occurrenceDate,
        reason: 'Rescheduled',
        new_date: o.newDate,
        new_start_time: o.newStart,
        new_end_time: o.newEnd,
      });
      toast.success('Practice updated');
      setEditingOccurrence(null);
      fetchClassExceptions();
    } catch (err) {
      toast.error('Failed to update practice');
    }
  };

  const handleResetOccurrence = async () => {
    const o = editingOccurrence;
    if (o?.exceptionId) await handleRestoreClassDate(o.exceptionId);
    setEditingOccurrence(null);
  };

  const handleOpenMeetDetail = async (meet) => {
    setSelectedMeetDetail(meet);
    setShowMeetDetailModal(true);
    setMeetChecklist([]);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/meets/${meet.id}/checklists`);
      setMeetChecklist(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching checklist:', err);
    }
  };

  const handleToggleChecklistItem = async (item) => {
    try {
      const endpoint = item.completed
        ? `${API_BASE_URL}/api/checklists/${item.id}/uncomplete`
        : `${API_BASE_URL}/api/checklists/${item.id}/complete`;
      await axios.put(endpoint, {});
      setMeetChecklist(prev => prev.map(c =>
        c.id === item.id ? { ...c, completed: c.completed ? 0 : 1 } : c
      ));
    } catch (err) {
      toast.error('Failed to update checklist item');
    }
  };

  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.trim() || !selectedMeetDetail) return;
    try {
      const response = await axios.post(`${API_BASE_URL}/api/meets/${selectedMeetDetail.id}/checklists`, {
        checklist_type: 'packing',
        item_name: newChecklistItem.trim(),
        is_required: false
      });
      setMeetChecklist(prev => [...prev, { id: response.data.id, item_name: newChecklistItem.trim(), completed: 0, checklist_type: 'packing' }]);
      setNewChecklistItem('');
    } catch (err) {
      toast.error('Failed to add checklist item');
    }
  };

  const handleDeleteChecklistItem = async (itemId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/checklists/${itemId}`);
      setMeetChecklist(prev => prev.filter(c => c.id !== itemId));
    } catch (err) {
      toast.error('Failed to delete checklist item');
    }
  };

  const handleBulkCreate = async () => {
    if (!bulkEvent.title || !bulkEvent.start_date || !bulkEvent.end_date || !bulkEvent.days_of_week.length) {
      toast.error('Fill in title, date range, and select at least one day of the week');
      return;
    }
    
    // Validate class assignment
    if (bulkEvent.assignTo === 'class' && !bulkEvent.assignClassId) {
      toast.error('Please select a class/roster');
      return;
    }
    
    // Validate athlete assignment
    if (bulkEvent.assignTo === 'athlete' && bulkEvent.assignAthleteIds.length === 0) {
      toast.error('Please select at least one athlete');
      return;
    }
    
    try {
      // If assigning to class, use the class-specific endpoint with target_groups
      if (bulkEvent.assignTo === 'class') {
        // Calculate all dates
        const dayIndexMap = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
        const targetDays = new Set(bulkEvent.days_of_week.map(d => dayIndexMap[d]));
        const cur = new Date(bulkEvent.start_date + 'T00:00:00');
        const last = new Date(bulkEvent.end_date + 'T00:00:00');
        
        let created = 0;
        while (cur <= last) {
          if (targetDays.has(cur.getDay())) {
            const dateStr = cur.toISOString().split('T')[0];
            const start_datetime = `${dateStr} ${bulkEvent.start_time}:00`;
            const end_datetime = `${dateStr} ${bulkEvent.end_time}:00`;
            
            await axios.post(`${API_BASE_URL}/api/classes/${bulkEvent.assignClassId}/events`, {
              title: bulkEvent.title,
              description: bulkEvent.description,
              event_type: bulkEvent.event_type,
              location_details: bulkEvent.location_details,
              start_datetime,
              end_datetime,
              all_day: 0
            });
            created++;
          }
          cur.setDate(cur.getDate() + 1);
        }
        
        setShowBulkModal(false);
        setBulkEvent({ 
          title: '', event_type: 'practice', location_id: '', location_details: '', 
          description: '', start_date: '', end_date: '', 
          start_time: '09:00', end_time: '10:00', days_of_week: [],
          assignTo: 'all', assignClassId: '', assignAthleteIds: []
        });
        fetchEvents();
        toast.success(`${created} practices created for the selected class!`);
      } else if (bulkEvent.assignTo === 'athlete') {
        // Create events with athlete target_groups
        const dayIndexMap = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
        const targetDays = new Set(bulkEvent.days_of_week.map(d => dayIndexMap[d]));
        const cur = new Date(bulkEvent.start_date + 'T00:00:00');
        const last = new Date(bulkEvent.end_date + 'T00:00:00');
        
        let created = 0;
        while (cur <= last) {
          if (targetDays.has(cur.getDay())) {
            const dateStr = cur.toISOString().split('T')[0];
            const start_datetime = `${dateStr} ${bulkEvent.start_time}:00`;
            const end_datetime = `${dateStr} ${bulkEvent.end_time}:00`;
            
            const payload = {
              title: bulkEvent.title,
              description: bulkEvent.description,
              event_type: bulkEvent.event_type,
              location_details: bulkEvent.location_details,
              start_datetime,
              end_datetime,
              all_day: 0,
              target_groups: JSON.stringify({ athlete_ids: bulkEvent.assignAthleteIds.map(id => parseInt(id)) })
            };
            
            await axios.post(`${API_BASE_URL}/api/calendar/events`, payload);
            created++;
          }
          cur.setDate(cur.getDate() + 1);
        }
        
        setShowBulkModal(false);
        setBulkEvent({ 
          title: '', event_type: 'practice', location_id: '', location_details: '', 
          description: '', start_date: '', end_date: '', 
          start_time: '09:00', end_time: '10:00', days_of_week: [],
          assignTo: 'all', assignClassId: '', assignAthleteIds: []
        });
        fetchEvents();
        toast.success(`${created} practices created for the selected athletes!`);
      } else {
        // Default: assign to all athletes (use bulk endpoint)
        const response = await axios.post(`${API_BASE_URL}/api/calendar/events/bulk`, {
          ...bulkEvent
        });
        setShowBulkModal(false);
        setBulkEvent({ 
          title: '', event_type: 'practice', location_id: '', location_details: '', 
          description: '', start_date: '', end_date: '', 
          start_time: '09:00', end_time: '10:00', days_of_week: [],
          assignTo: 'all', assignClassId: '', assignAthleteIds: []
        });
        fetchEvents();
        toast.success(`${response.data.count} practices created for all athletes!`);
      }
    } catch (err) {
      console.error('Error creating practices:', err);
      toast.error('Failed to create practices');
    }
  };

  const getDeadlineStatus = (registration_deadline) => {
    if (!registration_deadline) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadline = new Date(registration_deadline);
    const diffDays = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return { label: 'Registration closed', color: '#9ca3af' };
    if (diffDays === 0) return { label: 'Deadline today!', color: '#dc2626' };
    if (diffDays <= 3) return { label: `Deadline in ${diffDays}d`, color: '#dc2626' };
    if (diffDays <= 7) return { label: `Deadline in ${diffDays}d`, color: '#f59e0b' };
    return { label: `Deadline in ${diffDays}d`, color: '#10b981' };
  };

  const handleCreateEvent = async () => {
    try {
      if (!newEvent.title || !newEvent.start_datetime || !newEvent.end_datetime) {
        toast.error('Please fill in all required fields');
        return;
      }

      const payload = {
        title: newEvent.title,
        description: newEvent.description,
        event_type: newEvent.event_type,
        location_id: newEvent.location_id,
        location_details: newEvent.location_details,
        start_datetime: newEvent.start_datetime,
        end_datetime: newEvent.end_datetime,
        is_recurring: newEvent.is_recurring,
        recurrence_rule: newEvent.recurrence_rule,
        recurrence_end_date: newEvent.recurrence_end_date,
      };

      // Post a single event on the given day, honoring the chosen assignment
      // (all gymnasts / specific class / individual athletes).
      const createOne = async (start_datetime, end_datetime) => {
        const p = { ...payload, start_datetime, end_datetime };
        if (newEvent.assignTo === 'athlete' && newEvent.assignAthleteIds.length > 0) {
          const r = await axios.post(`${API_BASE_URL}/api/calendar/events`, {
            ...p,
            target_groups: JSON.stringify({ athlete_ids: newEvent.assignAthleteIds.map(id => parseInt(id)) })
          });
          return r.data?.eventId || null;
        } else if (newEvent.assignTo === 'class' && newEvent.assignClassId) {
          const r = await axios.post(`${API_BASE_URL}/api/classes/${newEvent.assignClassId}/events`, p);
          return r.data?.eventId || null;
        }
        const r = await axios.post(`${API_BASE_URL}/api/calendar/events`, p);
        return r.data?.eventId || null;
      };

      // Full day set = the start date plus any extra days the user picked. One real
      // event row is created per day so each shows on the calendar on that day.
      const baseDate = newEvent.start_datetime.split('T')[0];
      const startTime = newEvent.start_datetime.split('T')[1] || '09:00';
      const endTime = newEvent.end_datetime.split('T')[1] || '10:00';
      const extraDays = (newEvent.repeatDates || []).filter(Boolean);
      const allDates = Array.from(new Set([baseDate, ...extraDays])).sort();
      const multiDay = allDates.length > 1;

      let createdEventId = null;
      if (editingEvent) {
        await axios.put(`${API_BASE_URL}/api/calendar/events/${editingEvent.id}`, payload);
      } else if (multiDay) {
        for (const d of allDates) {
          const id = await createOne(`${d}T${startTime}`, `${d}T${endTime}`);
          if (!createdEventId) createdEventId = id;
        }
        toast.success(`Event added to ${allDates.length} days`);
      } else {
        createdEventId = await createOne(newEvent.start_datetime, newEvent.end_datetime);
      }

      // Capture practice details before resetForm() clears newEvent
      const wasNewPractice = newEvent.event_type === 'practice' && !editingEvent;
      const practiceDate = newEvent.start_datetime.split('T')[0];
      const practiceTitle = newEvent.title;

      setShowEventModal(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents();

      // Seamless: a newly-created single-day practice opens straight into the
      // unified planner (rotations, drills, coaches, theme & goals, workout, S&C).
      if (wasNewPractice && !multiDay && createdEventId) {
        setPracticePlan({
          classId: null,
          calendarEventId: createdEventId,
          date: practiceDate,
          className: practiceTitle
        });
      }
    } catch (err) {
      console.error('Error creating/updating event:', err);
      toast.error('Failed to save event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!await confirm({ title: 'Delete event?', message: 'Remove this event from the calendar?', confirmText: 'Delete', danger: true })) return;

    try {
      await axios.delete(`${API_BASE_URL}/api/calendar/events/${eventId}`);
      fetchEvents();
    } catch (err) {
      console.error('Error deleting event:', err);
      toast.error('Failed to delete event');
    }
  };

  const handleSeriesDelete = async (scope, eventArg) => {
    const event = eventArg || deleteConfirmEvent;
    if (!event) return;
    setDeleteConfirmEvent(null);
    try {
      if (scope === 'just_this') {
        await axios.delete(`${API_BASE_URL}/api/calendar/events/${event.id}`);
      } else {
        const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
        const eventDate = new Date(event.start_datetime);
        const day_of_week = dayNames[eventDate.getDay()];
        const from_date = scope === 'this_and_future' ? eventDate.toISOString().split('T')[0] : null;
        await axios.post(`${API_BASE_URL}/api/calendar/events/delete-by-pattern`, {
          title: event.title,
          event_type: event.event_type,
          day_of_week,
          from_date
        });
      }
      fetchEvents();
    } catch (err) {
      console.error('Error deleting event(s):', err);
      toast.error('Failed to delete');
    }
  };

  const handleBulkDeleteSearch = async () => {
    try {
      const params = {};
      if (bulkDeleteFilter.title) params.title = bulkDeleteFilter.title;
      if (bulkDeleteFilter.start_date) params.start_date = bulkDeleteFilter.start_date;
      if (bulkDeleteFilter.end_date) params.end_date = bulkDeleteFilter.end_date;
      params.event_type = 'practice';

      const response = await axios.get(`${API_BASE_URL}/api/calendar/events/search`, { params });
      const results = Array.isArray(response.data) ? response.data : [];
      setBulkDeleteMatches(results);
      setBulkDeleteSelected([]);
      setBulkDeleteSearched(true);
    } catch (err) {
      console.error('Error searching events:', err);
      toast.error('Failed to search events');
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (bulkDeleteSelected.length === 0) { toast.error('No practices selected'); return; }
    if (!await confirm({ title: 'Delete practices?', message: `Delete ${bulkDeleteSelected.length} practice${bulkDeleteSelected.length > 1 ? 's' : ''}? This cannot be undone.`, confirmText: 'Delete', danger: true })) return;
    try {
      await axios.post(`${API_BASE_URL}/api/calendar/events/bulk-delete`, { ids: bulkDeleteSelected });
      setShowBulkDeleteModal(false);
      fetchEvents();
      toast.success(`${bulkDeleteSelected.length} practice${bulkDeleteSelected.length > 1 ? 's' : ''} deleted.`);
    } catch (err) {
      console.error('Error bulk deleting events:', err);
      toast.error('Failed to delete practices');
    }
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setNewEvent({
      title: event.title,
      description: event.description || '',
      event_type: event.event_type,
      location_id: event.location_id || '',
      location_details: event.location_details || '',
      start_datetime: event.start_datetime,
      end_datetime: event.end_datetime,
      is_recurring: event.is_recurring || false,
      recurrence_rule: event.recurrence_rule || '',
      recurrence_end_date: event.recurrence_end_date || ''
    });
    setShowEventModal(true);
  };

  const resetForm = () => {
    setNewEvent({
      title: '',
      description: '',
      event_type: 'practice',
      location_id: '',
      location_details: '',
      start_datetime: '',
      end_datetime: '',
      is_recurring: false,
      recurrence_rule: '',
      recurrence_end_date: '',
      assignTo: 'all',
      assignClassId: '',
      assignAthleteIds: [],
      repeatDates: []
    });
    setRepeatDateInput('');
  };

  const openCreateForDay = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    const defaultType = isAdminOrCoach ? 'practice' : 'personal';
    setNewEvent({
      title: '', description: '', event_type: defaultType,
      location_id: '', location_details: '',
      start_datetime: `${dateStr}T09:00`,
      end_datetime: `${dateStr}T10:00`,
      is_recurring: false, recurrence_rule: '', recurrence_end_date: '',
      assignTo: selectedClassFilter ? 'class' : 'all',
      assignClassId: selectedClassFilter ? String(selectedClassFilter) : '',
      assignAthleteIds: [],
      repeatDates: []
    });
    setRepeatDateInput('');
    setEditingEvent(null);
    setShowEventModal(true);
  };

  // Calendar rendering helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Previous month days (empty cells before the 1st)
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    // Fill remaining cells to complete the grid (always show 6 weeks = 42 cells)
    const remainingCells = 42 - days.length;
    for (let i = 0; i < remainingCells; i++) {
      days.push(null);
    }

    return days;
  };

  const getEventsForDay = (date) => {
    if (!date) return [];
    
    // Regular calendar events for this day
    const dayEvents = events.filter(event => {
      const eventDate = new Date(event.start_datetime);
      const matches = (
        eventDate.getDate() === date.getDate() &&
        eventDate.getMonth() === date.getMonth() &&
        eventDate.getFullYear() === date.getFullYear()
      );
      if (date.getDate() === 28 && date.getMonth() === 2) { // Log for March 28
        console.log(`[${date.toDateString()}] Checking event "${event.title}":`, {
          eventDateTime: event.start_datetime,
          parsedDate: eventDate.toISOString(),
          eventDay: eventDate.getDate(),
          eventMonth: eventDate.getMonth(),
          eventYear: eventDate.getFullYear(),
          dateDay: date.getDate(),
          dateMonth: date.getMonth(),
          dateYear: date.getFullYear(),
          matches
        });
      }
      return matches;
    });
    
    // Class schedules (recurring weekly), respecting season ranges + per-occurrence
    // overrides (cancel or reschedule).
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayOfWeek = dayNames[date.getDay()];
    const dateStr = date.toISOString().split('T')[0];

    const dstr = (v) => (v ? String(v).slice(0, 10) : null);
    const inSeason = (s) => {
      const sd = dstr(s.start_date), ed = dstr(s.end_date);
      if (sd && dateStr < sd) return false;   // before the season starts
      if (ed && dateStr > ed) return false;    // after the season ends
      return true;
    };
    // An exception is a "move" (reschedule) when any override field is set; else a cancel.
    const isMove = (ex) => !!(ex.new_date || ex.new_start_time || ex.new_end_time);

    const makeClassEvent = (schedule, occStart, occEnd, opts = {}) => ({
      id: `class-${schedule.id}-${dateStr}${opts.moved ? '-moved' : ''}`,
      title: schedule.athlete_name
        ? `${schedule.class_name} (${schedule.athlete_name})`
        : schedule.class_name,
      event_type: 'class',
      start_datetime: `${dateStr}T${occStart}`,
      end_datetime: `${dateStr}T${occEnd}`,
      class_level: schedule.level,
      scheduleId: schedule.id,
      scheduleData: schedule,
      isClassSchedule: true,
      isMoved: !!opts.moved,
      exceptionId: opts.exceptionId || null,
      exceptionDate: opts.exceptionDate || dateStr,
    });

    // 1) Recurring occurrences for this weekday, in-season, minus any date that
    //    has an exception (cancelled OR moved away — the moved copy is drawn below).
    const recurring = classSchedules
      .filter((s) => s.day_of_week === dayOfWeek && inSeason(s))
      .filter((s) => !classExceptions.some(
        (ex) => ex.class_schedule_id === s.id && dstr(ex.exception_date) === dateStr
      ))
      .map((s) => makeClassEvent(s, s.start_time, s.end_time));

    // 2) Rescheduled occurrences that LAND on this date (moved here / retimed).
    const moved = classExceptions
      .filter((ex) => isMove(ex) && dstr(ex.new_date || ex.exception_date) === dateStr)
      .map((ex) => {
        const s = classSchedules.find((cs) => cs.id === ex.class_schedule_id);
        if (!s) return null;
        return makeClassEvent(
          s,
          ex.new_start_time || s.start_time,
          ex.new_end_time || s.end_time,
          { moved: true, exceptionId: ex.id, exceptionDate: dstr(ex.exception_date) }
        );
      })
      .filter(Boolean);

    const classEvents = [...recurring, ...moved];

    // Cancelled class badges (so coaches can restore them)
    const cancelledClassEvents = classSchedules
      .filter(schedule => {
        if (schedule.day_of_week !== dayOfWeek) return false;
        const exception = classExceptions.find(
          ex => ex.class_schedule_id === schedule.id && ex.exception_date.startsWith(dateStr)
        );
        return !!exception;
      })
      .map(schedule => {
        const exception = classExceptions.find(
          ex => ex.class_schedule_id === schedule.id && ex.exception_date.startsWith(dateStr)
        );
        return {
          id: `cancelled-class-${schedule.id}-${dateStr}`,
          title: `❌ ${schedule.class_name} (${exception.reason})`,
          event_type: 'class',
          start_datetime: `${dateStr}T${schedule.start_time}`,
          end_datetime: `${dateStr}T${schedule.end_time}`,
          isClassSchedule: true,
          isCancelledClass: true,
          exceptionId: exception.id,
          scheduleData: schedule
        };
      });

    // Competition/meet events for this day
    const meetEvents = competitionEvents
      .filter(meet => {
        const meetDate = new Date(meet.event_date);
        return (
          meetDate.getUTCDate() === date.getDate() &&
          meetDate.getUTCMonth() === date.getMonth() &&
          meetDate.getUTCFullYear() === date.getFullYear()
        );
      })
      .map(meet => ({
        id: `meet-${meet.id}`,
        title: meet.event_name,
        event_type: meet.event_type,
        start_datetime: `${meet.event_date}T09:00:00`,
        end_datetime: `${meet.event_date}T17:00:00`,
        location_details: meet.location_details || '',
        description: meet.description,
        deadlineStatus: getDeadlineStatus(meet.registration_deadline),
        meetData: meet,
        isMeetEvent: true
      }));
    
    return [...dayEvents, ...classEvents, ...cancelledClassEvents, ...meetEvents];
  };

  const getFilteredEventsForDay = (date) => {
    const all = getEventsForDay(date);
    if (date.getDate() === 28 && date.getMonth() === 2) {
      console.log(`[${date.toDateString()}] getFilteredEventsForDay:`, {
        totalEvents: all.length,
        selectedClassFilter,
        events: all.map(e => ({ title: e.title, type: e.event_type, isMeet: e.isMeetEvent, target: e.target_groups }))
      });
    }
    if (!selectedClassFilter) return all;
    const filtered = all.filter(event => {
      if (event.isMeetEvent) return true;
      if (event.isClassSchedule) return event.scheduleData?.class_id === selectedClassFilter;
      if (!event.target_groups) return true;
      try {
        const tg = typeof event.target_groups === 'string' ? JSON.parse(event.target_groups) : event.target_groups;
        if (!tg || (!tg.class_ids?.length && !tg.athlete_ids?.length)) return true;
        const matches = tg.class_ids?.includes(selectedClassFilter);
        if (date.getDate() === 28) {
          console.log(`  Filtering "${event.title}": classFilter=${selectedClassFilter}, targetClasses=${tg.class_ids}, matches=${matches}`);
        }
        return matches;
      } catch { return true; }
    });
    if (date.getDate() === 28 && date.getMonth() === 2) {
      console.log(`  → Filtered to ${filtered.length} events`);
    }
    return filtered;
  };

  const formatTime = (datetime) => {
    return new Date(datetime).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (datetime) => {
    return new Date(datetime).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const changeMonth = (direction) => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + direction, 1));
  };

  const getEventTypeColor = (type) => {
    const colors = {
      practice: '#3b82f6',
      meet: '#ef4444',
      competition: '#f59e0b',
      showcase: '#8b5cf6',
      invitational: '#f59e0b',
      championship: '#dc2626',
      class: '#10b981',
      one_on_one: '#8b5cf6',
      video_review: '#06b6d4',
      athlete_event: '#ec4899',
      class_event: '#10b981',
      tuition_due: '#f97316',
      equipment_due: '#a855f7',
      form_due: '#dc2626',
      deadline: '#b45309',
      personal: '#6b7280',
      other: '#6b7280'
    };
    return colors[type] || colors.other;
  };

  const getTargetLabel = (event) => {
    if (!event.target_groups) return null;
    try {
      const tg = typeof event.target_groups === 'string'
        ? JSON.parse(event.target_groups)
        : event.target_groups;
      if (!tg) return null;
      if (tg.athlete_ids?.length) {
        const a = allAthletes.find(at => at.id === tg.athlete_ids[0]);
        return `→ ${a?.name || 'Athlete'}`;
      }
      if (tg.class_ids?.length) {
        const c = allClasses.find(cl => cl.id === tg.class_ids[0]);
        return `→ ${c?.class_name || 'Class'}`;
      }
    } catch {}
    return null;
  };

  const navigate = useNavigate();
  const isAdminOrCoach = user && (user.role === 'admin' || user.role === 'coach');
  const activeClassName = allClasses.find(c => c.id === selectedClassFilter)?.class_name;

  return (
    <div className="schedule-page">
      {confirmModal}

      {/* Edit a single practice occurrence (retime / move date) */}
      {editingOccurrence && (
        <div className="modal-overlay" onClick={() => setEditingOccurrence(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit practice — {editingOccurrence.schedule.class_name}</h2>
            <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '-8px' }}>
              Changes this one practice only; the rest of the recurring schedule is unchanged.
            </p>
            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                value={editingOccurrence.newDate}
                onChange={(e) => setEditingOccurrence({ ...editingOccurrence, newDate: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Start time</label>
              <input
                type="time"
                value={editingOccurrence.newStart}
                onChange={(e) => setEditingOccurrence({ ...editingOccurrence, newStart: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>End time</label>
              <input
                type="time"
                value={editingOccurrence.newEnd}
                onChange={(e) => setEditingOccurrence({ ...editingOccurrence, newEnd: e.target.value })}
              />
            </div>
            <div className="modal-buttons" style={{ justifyContent: 'space-between' }}>
              {editingOccurrence.exceptionId ? (
                <button type="button" className="btn-secondary" onClick={handleResetOccurrence}>
                  Reset to regular time
                </button>
              ) : <span />}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="button" className="btn-secondary" onClick={() => setEditingOccurrence(null)}>
                  Cancel
                </button>
                <button type="button" className="btn-primary" onClick={handleSaveOccurrence}>
                  Save changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ── Header ── */}
      <div className="schedule-header">
        <div className="schedule-title-area">
          <h1>🗓 Schedule</h1>
          {activeClassName && <span className="active-filter-badge">{activeClassName}</span>}
        </div>
        <div className="calendar-controls">
          <Button variant="ghost" size="sm" onClick={() => changeMonth(-1)}>←</Button>
          <h2>{selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h2>
          <Button variant="ghost" size="sm" onClick={() => changeMonth(1)}>→</Button>
        </div>
        <div className="schedule-header-right">
          <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
            <button
              onClick={() => setView('month')}
              className={`px-3 py-1.5 text-sm font-medium ${view === 'month' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >Month</button>
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 text-sm font-medium border-l border-gray-200 ${view === 'list' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >List</button>
          </div>
          {isAdminOrCoach && (
            <>
              <Button variant="primary" onClick={() => { resetForm(); setShowEventModal(true); }}>➕ New Event</Button>
              <Button variant="success" onClick={() => setShowBulkModal(true)}>📋 Create Practice</Button>
            </>
          )}
          {!isAdminOrCoach && (
            <Button variant="secondary" onClick={() => { resetForm(); setNewEvent(prev => ({ ...prev, event_type: 'personal' })); setShowEventModal(true); }}>
              🔒 Add to My Calendar
            </Button>
          )}
        </div>
      </div>

      {/* ── Workspace ── */}
      <div className={`schedule-workspace${isAdminOrCoach ? '' : ' no-sidebar'}`}>

        {/* Left: class filter sidebar - admin/coach only */}
        {isAdminOrCoach && (
          <aside className="schedule-sidebar">
            <button
              className={`class-pill${!selectedClassFilter ? ' active' : ''}`}
              onClick={() => { setSelectedClassFilter(null); setClickedDay(null); }}
            >
              <span className="class-pill-name">👥 All Athletes</span>
            </button>
            <div className="sidebar-divider">Classes</div>
            {allClasses.map(cls => (
              <button
                key={cls.id}
                className={`class-pill${selectedClassFilter === cls.id ? ' active' : ''}`}
                onClick={() => { setSelectedClassFilter(cls.id); setClickedDay(null); }}
              >
                <span className="class-pill-name">{cls.class_name}</span>
                {cls.level && <span className="class-pill-level">{cls.level}</span>}
              </button>
            ))}
            <div className="sidebar-footer">
              <a href="/class-management" className="manage-classes-link">⚙️ Manage Classes</a>
            </div>
          </aside>
        )}

        {/* Right: calendar + day panel */}
        <div className="schedule-main">

      {loading ? (
        <LoadingScreen message="Loading calendar…" />
      ) : (
        <>
          {view === 'month' && (
            <div className="tcal-month-grid">
              <div className="calendar-weekdays">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="weekday">{day}</div>
                ))}
              </div>
              <div className="calendar-days">
                {getDaysInMonth(selectedDate).map((date, index) => {
                  const dayEvents = date ? getFilteredEventsForDay(date) : [];
                  const isToday = date && 
                    date.getDate() === new Date().getDate() &&
                    date.getMonth() === new Date().getMonth() &&
                    date.getFullYear() === new Date().getFullYear();
                  const isSelected = clickedDay && date && clickedDay.toDateString() === date.toDateString();

                  return (
                    <div 
                      key={index} 
                      className={`calendar-day ${!date ? 'empty' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                      onClick={() => date && setClickedDay(date)}
                      style={{ cursor: date ? 'pointer' : 'default' }}
                    >
                      {date && (
                        <>
                          <div className="day-number">{date.getDate()}</div>
                          {user && (user.role === 'admin' || user.role === 'coach') && (() => {
                            const dateStr = date.toISOString().split('T')[0];
                            const vs = videoSummary[dateStr];
                            if (!vs) return null;
                            return (
                              <div className="video-day-chip" title={`Videos: ${vs.athlete_names}`}>
                                🎥 {vs.count}
                              </div>
                            );
                          })()}
                          <div className="day-events">
                            {dayEvents.slice(0, 3).map(event => (
                              <div 
                                key={event.id} 
                                className={`event-badge${event.isCancelledClass ? ' cancelled-class' : ''}`}
                                style={{ borderLeft: `4px solid ${event.isCancelledClass ? '#d1d5db' : getEventTypeColor(event.event_type)}`, opacity: event.isCancelledClass ? 0.5 : 1 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (event.isMeetEvent) return handleOpenMeetDetail(event.meetData);
                                  if (event.isClassSchedule) return;
                                  setSelectedEventCard(event);
                                }}
                              >
                                {event.event_type === 'personal' && <span style={{ fontSize: '9px', marginRight: '2px' }}>🔒</span>}
                                <span className="event-time">{formatTime(event.start_datetime)}</span>
                                <span className="event-title">{event.title}</span>
                                {getTargetLabel(event) && (
                                  <span className="event-target-label">{getTargetLabel(event)}</span>
                                )}
                                {event.deadlineStatus && (
                                  <span style={{ fontSize: '9px', color: event.deadlineStatus.color, display: 'block' }}>
                                    {event.deadlineStatus.label}
                                  </span>
                                )}
                                {(() => {
                                  const plan = planForEvent(event, date);
                                  if (!plan) return null;
                                  const timed = (plan.slots || []).filter(s => s.start_time);
                                  const rotPreview = timed.slice(0, 4).map(s => `${s.label} ${s.start_time}`).join(' · ');
                                  return (
                                    <div className="event-plan" title={plan.theme ? `Plan: ${plan.theme}` : 'Practice plan'}>
                                      <span className="event-plan-theme">📋 {plan.theme || 'Planned'}</span>
                                      {rotPreview && (
                                        <span className="event-plan-rot">
                                          {rotPreview}{timed.length > 4 ? ' …' : ''}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })()}
                                {event.isClassSchedule && !event.isCancelledClass && user && (user.role === 'admin' || user.role === 'coach') && (
                                  <>
                                    <button
                                      className="btn-cancel-class"
                                      title={`Plan ${event.title} practice (rotations, workout, S&C, theme)`}
                                      onClick={(e) => { e.stopPropagation(); openPracticePlan(event, date); }}
                                    >📋</button>
                                    <button
                                      className="btn-cancel-class"
                                      title={`Edit this ${event.title} practice (time / date)`}
                                      onClick={(e) => { e.stopPropagation(); openEditOccurrence(event); }}
                                    >✏️</button>
                                    <button
                                      className="btn-cancel-class"
                                      title={`Cancel ${event.title} on this date`}
                                      onClick={(e) => { e.stopPropagation(); handleCancelClassDate(event.scheduleData, date); }}
                                    >✕</button>
                                  </>
                                )}
                                {event.isCancelledClass && user && (user.role === 'admin' || user.role === 'coach') && (
                                  <button
                                    className="btn-restore-class"
                                    title="Restore this class"
                                    onClick={(e) => { e.stopPropagation(); handleRestoreClassDate(event.exceptionId); }}
                                  >↩</button>
                                )}
                              </div>
                            ))}
                            {dayEvents.length > 3 && (
                              <div className="more-events">+{dayEvents.length - 3} more</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view === 'list' && (
            <div className="events-list">
              {(() => {
                // Build list of all events for the month
                const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
                const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);

                // Expand weekly class schedules into individual occurrences for this month
                const expandedClassEvents = [];
                const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                for (let d = new Date(startOfMonth); d <= endOfMonth; d.setDate(d.getDate() + 1)) {
                  const dow = dayNames[d.getDay()];
                  classSchedules
                    .filter(s => s.day_of_week === dow)
                    .forEach(s => {
                      expandedClassEvents.push({
                        id: `class-${s.id}-${d.toISOString()}`,
                        title: s.athlete_name ? `${s.class_name} (${s.athlete_name})` : s.class_name,
                        event_type: 'class',
                        start_datetime: `${d.toISOString().split('T')[0]}T${s.start_time}`,
                        end_datetime: `${d.toISOString().split('T')[0]}T${s.end_time}`,
                        isClassSchedule: true
                      });
                    });
                }

                // Map competition events
                const mappedMeetEvents = competitionEvents.map(meet => ({
                  id: `meet-${meet.id}`,
                  title: meet.event_name,
                  event_type: meet.event_type,
                  start_datetime: `${meet.event_date}T09:00:00`,
                  end_datetime: `${meet.event_date}T17:00:00`,
                  location_details: meet.location_details || '',
                  description: meet.description,
                  deadlineStatus: getDeadlineStatus(meet.registration_deadline),
                  meetData: meet,
                  isMeetEvent: true
                }));

                const allEvents = [...events, ...expandedClassEvents, ...mappedMeetEvents]
                  .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));

                if (allEvents.length === 0) {
                  return <div className="no-events">No events this month</div>;
                }

                return allEvents.map(event => (
                  <div
                    key={event.id}
                    className="event-list-item"
                    style={{
                      borderLeft: `4px solid ${event.isCancelledClass ? '#d1d5db' : getEventTypeColor(event.event_type)}`,
                      opacity: event.isCancelledClass ? 0.5 : 1,
                      cursor: event.isMeetEvent ? 'pointer' : undefined
                    }}
                    onClick={() => event.isMeetEvent && handleOpenMeetDetail(event.meetData)}
                  >
                    <div className="event-list-header">
                      <div>
                        <h3>{event.title}</h3>
                        <span className="event-type-badge">{event.event_type}</span>
                        {getTargetLabel(event) && (
                          <span className="event-target-badge">{getTargetLabel(event)}</span>
                        )}
                        {event.deadlineStatus && (
                          <span style={{ marginLeft: 8, fontSize: '12px', fontWeight: 600, color: event.deadlineStatus.color }}>
                            🗓 {event.deadlineStatus.label}
                          </span>
                        )}
                      </div>
                      {!event.isClassSchedule && !event.isMeetEvent && user && (user.role === 'admin' || user.role === 'coach') && (
                        <div className="event-actions">
                          <button onClick={(e) => { e.stopPropagation(); handleEditEvent(event); }} className="btn-icon">✏️</button>
                          <button onClick={(e) => { e.stopPropagation(); setDeleteConfirmEvent(event); }} className="btn-icon delete">🗑️</button>
                        </div>
                      )}
                      {event.isClassSchedule && !event.isCancelledClass && user && (user.role === 'admin' || user.role === 'coach') && (
                        <>
                          <button
                            className="btn-secondary"
                            style={{ fontSize: '12px' }}
                            onClick={(e) => { e.stopPropagation(); openPracticePlan(event, new Date(event.start_datetime)); }}
                          >📋 Plan</button>
                          <button
                            className="btn-secondary"
                            style={{ fontSize: '12px' }}
                            onClick={(e) => { e.stopPropagation(); openEditOccurrence(event); }}
                          >Edit</button>
                          <button
                            className="btn-secondary"
                            style={{ fontSize: '12px' }}
                            onClick={(e) => { e.stopPropagation(); handleCancelClassDate(event.scheduleData, new Date(event.start_datetime)); }}
                          >Cancel date</button>
                        </>
                      )}
                      {event.isCancelledClass && user && (user.role === 'admin' || user.role === 'coach') && (
                        <button
                          className="btn-secondary"
                          style={{ fontSize: '12px' }}
                          onClick={(e) => { e.stopPropagation(); handleRestoreClassDate(event.exceptionId); }}
                        >Restore</button>
                      )}
                    </div>
                    <div className="event-list-details">
                      <div>📅 {formatDate(event.start_datetime)}</div>
                      <div>🕐 {formatTime(event.start_datetime)} - {formatTime(event.end_datetime)}</div>
                      {event.location_details && <div>📍 {event.location_details}</div>}
                      {event.description && <p>{event.description}</p>}
                      {event.isMeetEvent && <div style={{ fontSize: '12px', color: '#6b7280' }}>Click to view details & checklist</div>}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </>
      )}

      {/* ── Day detail panel ── */}
      {clickedDay && (() => {
        const dateStr = clickedDay.toISOString().split('T')[0];
        const vs = videoSummary[dateStr];
        const dayEvs = getFilteredEventsForDay(clickedDay);
        const isTodayDay = clickedDay.toDateString() === new Date().toDateString();
        return (
          <div className="day-panel">
            <div className="day-panel-header">
              <h3>
                {clickedDay.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                {isTodayDay && <span className="today-tag">Today</span>}
              </h3>
              <div className="day-panel-actions">
                <button className="btn-primary btn-sm" onClick={() => openCreateForDay(clickedDay)}>
                  ➕ {isAdminOrCoach ? (activeClassName ? `Event for ${activeClassName}` : 'Add Event') : 'Add to My Calendar'}
                </button>
                <button className="btn-close" onClick={() => setClickedDay(null)}>×</button>
              </div>
            </div>
            {vs && (
              <div className="day-panel-videos">
                {isAdminOrCoach ? (
                  <>
                    <div className="day-panel-section-title">🎥 {vs.count} Video{vs.count > 1 ? 's' : ''} Recorded</div>
                    <p className="day-panel-athletes">{vs.athlete_names}</p>
                  </>
                ) : (
                  <>
                    <div className="day-panel-section-title">🎥 {vs.count} Video{vs.count > 1 ? 's' : ''} Recorded</div>
                    <div className="day-panel-video-list">
                      {(athleteVideos[dateStr] || []).map(video => (
                        <div key={video.id} className="day-panel-video-item">
                          <span className="day-panel-video-time">
                            ⏰ {new Date(video.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                            {video.event && <span className="day-panel-video-event"> · {video.event}</span>}
                          </span>
                          <EnhancedVideoPlayer
                            videoSrc={video.r2_url || `${API_BASE_URL}/videos/${video.file_name}`}
                            videoId={video.id}
                          />
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="day-panel-events">
              {dayEvs.length === 0 ? (
                <p className="day-panel-empty">No events scheduled — click “Add Event” above to create one.</p>
              ) : (
                (() => {
                  const personalEvs = dayEvs.filter(e => e.event_type === 'personal' && e.created_by === user?.id);
                  const teamEvs = dayEvs.filter(e => !(e.event_type === 'personal' && e.created_by === user?.id));
                  const renderEvent = (event) => (
                    <div
                      key={event.id}
                      className="day-panel-event"
                      style={{ borderLeft: `4px solid ${event.isCancelledClass ? '#d1d5db' : getEventTypeColor(event.event_type)}`, cursor: (!event.isClassSchedule && !event.isMeetEvent && (isAdminOrCoach || event.event_type === 'personal')) ? 'pointer' : 'default' }}
                      onClick={() => { if (!event.isClassSchedule && !event.isMeetEvent && (isAdminOrCoach || event.event_type === 'personal')) setSelectedEventCard(event); }}
                    >
                      <div className="day-panel-event-time">{formatTime(event.start_datetime)}</div>
                      <div className="day-panel-event-details">
                        <div className="day-panel-event-title">
                          {event.event_type === 'personal' && <span style={{ marginRight: '4px' }}>🔒</span>}
                          {event.title}
                        </div>
                        {event.location_details && <div className="day-panel-event-loc">📍 {event.location_details}</div>}
                        {getTargetLabel(event) && <div className="day-panel-target">{getTargetLabel(event)}</div>}
                      </div>
                    </div>
                  );
                  return (
                    <>
                      {teamEvs.length > 0 && (
                        <>
                          <div className="day-panel-section-title" style={{ marginBottom: 8 }}>📋 Schedule</div>
                          {teamEvs.map(renderEvent)}
                        </>
                      )}
                      {personalEvs.length > 0 && (
                        <>
                          <div className="day-panel-section-title" style={{ marginBottom: 8, marginTop: teamEvs.length > 0 ? 16 : 0 }}>🔒 My Events</div>
                          {personalEvs.map(renderEvent)}
                        </>
                      )}
                    </>
                  );
                })()
              )}
            </div>
          </div>
        );
      })()}

        </div>{/* end .schedule-main */}
      </div>{/* end .schedule-workspace */}

      {/* Event Modal */}
      {showEventModal && (
        <div className="modal-overlay" onClick={() => setShowEventModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingEvent ? 'Edit Event' : (isAdminOrCoach ? 'Create New Event' : 'Add to My Calendar')}</h2>
              <button onClick={() => {
                setShowEventModal(false);
                setEditingEvent(null);
                resetForm();
              }} className="btn-close">×</button>
            </div>
            <div className="modal-body">
              {!isAdminOrCoach && (
                <div style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '0.875rem', color: '#374151' }}>
                  🔒 This event will only be visible to you
                </div>
              )}
              <div className="form-group">
                <label>Event Title *</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                  placeholder={isAdminOrCoach ? 'e.g., Practice, Competition' : 'e.g., Doctor appt, Travel, Reminder'}
                />
              </div>

              {isAdminOrCoach && (
                <div className="form-group">
                  <label>Event Type</label>
                  <select
                    value={newEvent.event_type}
                    onChange={(e) => setNewEvent({...newEvent, event_type: e.target.value})}
                  >
                    <optgroup label="Team Events">
                      <option value="practice">Practice</option>
                      <option value="meet">Meet</option>
                      <option value="competition">Competition</option>
                      <option value="one_on_one">One-on-One</option>
                      <option value="video_review">Video Review</option>
                    </optgroup>
                    <optgroup label="Due Dates &amp; Deadlines">
                      <option value="tuition_due">💰 Tuition Due</option>
                      <option value="equipment_due">🌿 Equipment Due (leotards, grips, etc.)</option>
                      <option value="form_due">📝 Form / Paperwork Due</option>
                      <option value="deadline">⚠️ Deadline</option>
                    </optgroup>
                    <optgroup label="Other">
                      <option value="personal">🔒 Personal (just me)</option>
                      <option value="other">Other</option>
                    </optgroup>
                  </select>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date & Time *</label>
                  <input
                    type="datetime-local"
                    value={newEvent.start_datetime}
                    onChange={(e) => setNewEvent({...newEvent, start_datetime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>End Date & Time *</label>
                  <input
                    type="datetime-local"
                    value={newEvent.end_datetime}
                    onChange={(e) => setNewEvent({...newEvent, end_datetime: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Location & Travel Details</label>
                <textarea
                  value={newEvent.location_details}
                  onChange={(e) => setNewEvent({...newEvent, location_details: e.target.value})}
                  rows="4"
                  placeholder="Address, venue name, hotels, restaurants, flights, parking info, etc."
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
                  rows="3"
                  placeholder="Optional notes about this event"
                />
              </div>

              {!editingEvent && isAdminOrCoach && newEvent.event_type !== 'personal' && (
                <div className="form-group">
                  <label>Assign to</label>
                  <div className="assign-to-options">
                    <label className="assign-radio">
                      <input type="radio" name="assignTo" value="all"
                        checked={newEvent.assignTo === 'all'}
                        onChange={() => setNewEvent({...newEvent, assignTo: 'all', assignClassId: '', assignAthleteIds: []})}
                      /> 🏫 All gymnasts
                    </label>
                    <label className="assign-radio">
                      <input type="radio" name="assignTo" value="class"
                        checked={newEvent.assignTo === 'class'}
                        onChange={() => setNewEvent({...newEvent, assignTo: 'class', assignAthleteIds: []})}
                      /> 📋 Specific class / level
                    </label>
                    <label className="assign-radio">
                      <input type="radio" name="assignTo" value="athlete"
                        checked={newEvent.assignTo === 'athlete'}
                        onChange={() => setNewEvent({...newEvent, assignTo: 'athlete', assignClassId: ''})}
                      /> 👤 Individual athlete(s)
                    </label>
                  </div>
                  {newEvent.assignTo === 'class' && (
                    <select
                      className="assign-select"
                      value={newEvent.assignClassId}
                      onChange={(e) => setNewEvent({...newEvent, assignClassId: e.target.value})}
                    >
                      <option value="">Select class...</option>
                      {allClasses.map(c => (
                        <option key={c.id} value={c.id}>{c.class_name}{c.level ? ` — ${c.level}` : ''}</option>
                      ))}
                    </select>
                  )}
                  {newEvent.assignTo === 'athlete' && (
                    <div className="athlete-multi-select">
                      <div className="athlete-select-label">Select athletes (check all that apply):</div>
                      <div className="athlete-checkboxes">
                        {allAthletes.map(athlete => (
                          <label key={athlete.id} className="athlete-checkbox">
                            <input
                              type="checkbox"
                              checked={newEvent.assignAthleteIds.includes(String(athlete.id))}
                              onChange={(e) => {
                                const athleteIds = e.target.checked
                                  ? [...newEvent.assignAthleteIds, String(athlete.id)]
                                  : newEvent.assignAthleteIds.filter(id => id !== String(athlete.id));
                                setNewEvent({...newEvent, assignAthleteIds: athleteIds});
                              }}
                            />
                            {athlete.name}
                          </label>
                        ))}
                      </div>
                      {newEvent.assignAthleteIds.length > 0 && (
                        <div className="selected-count">
                          {newEvent.assignAthleteIds.length} athlete{newEvent.assignAthleteIds.length > 1 ? 's' : ''} selected
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!editingEvent && (
                <div className="form-group">
                  <label>Repeat on more days (optional)</label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="date"
                      value={repeatDateInput}
                      onChange={(e) => setRepeatDateInput(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        const d = repeatDateInput;
                        if (!d) return;
                        const baseDate = newEvent.start_datetime ? newEvent.start_datetime.split('T')[0] : '';
                        if (d === baseDate) { toast.error("That's already the event's start date"); return; }
                        if ((newEvent.repeatDates || []).includes(d)) { setRepeatDateInput(''); return; }
                        setNewEvent({ ...newEvent, repeatDates: [...(newEvent.repeatDates || []), d].sort() });
                        setRepeatDateInput('');
                      }}
                    >
                      + Add day
                    </button>
                  </div>
                  {(newEvent.repeatDates || []).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, alignItems: 'center' }}>
                      {newEvent.start_datetime && (
                        <span style={{ background: '#eef2f7', color: '#2c3e50', borderRadius: 999, padding: '3px 10px', fontSize: '0.82rem' }}>
                          {newEvent.start_datetime.split('T')[0]} · start
                        </span>
                      )}
                      {(newEvent.repeatDates || []).map(d => (
                        <span key={d} style={{ background: '#e8f0fe', color: '#1a3e72', borderRadius: 999, padding: '3px 6px 3px 10px', fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          {d}
                          <button
                            type="button"
                            onClick={() => setNewEvent({ ...newEvent, repeatDates: (newEvent.repeatDates || []).filter(x => x !== d) })}
                            style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#1a3e72', fontSize: '1rem', lineHeight: 1 }}
                            aria-label={`Remove ${d}`}
                          >×</button>
                        </span>
                      ))}
                      <span style={{ fontSize: '0.8rem', color: '#7f8c8d', width: '100%' }}>
                        Creates this event on {(newEvent.repeatDates || []).length + 1} days (same time each day).
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={() => {
                setShowEventModal(false);
                setEditingEvent(null);
                resetForm();
              }} className="btn-secondary">
                Cancel
              </button>
              <button onClick={handleCreateEvent} className="btn-primary">
                {editingEvent ? 'Update Event' : 'Create Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Detail Card (Teams-style — click event to open) */}
      {selectedEventCard && (() => {
        const ev = selectedEventCard;
        const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
        const evDate = new Date(ev.start_datetime);
        const dayName = dayNames[evDate.getDay()];
        const isPractice = ev.event_type === 'practice';
        const dateLabel = evDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        const timeLabel = `${formatTime(ev.start_datetime)} – ${formatTime(ev.end_datetime)}`;
        return (
          <div className="modal-overlay" onClick={() => setSelectedEventCard(null)}>
            <div className="modal-content" style={{ maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
              {/* Color bar */}
              <div style={{ height: '5px', background: getEventTypeColor(ev.event_type), borderRadius: '12px 12px 0 0', margin: '-0px' }} />
              <div className="modal-header" style={{ paddingTop: '14px' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.15rem' }}>{ev.title}</h2>
                  <span className="event-type-badge" style={{ marginTop: '4px', display: 'inline-block' }}>{ev.event_type}</span>
                </div>
                <button onClick={() => setSelectedEventCard(null)} className="btn-close">×</button>
              </div>
              <div className="modal-body" style={{ paddingTop: '8px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px', color: '#374151', fontSize: '0.9rem' }}>
                  <div>📅 {dateLabel}</div>
                  <div>🕐 {timeLabel}</div>
                  {ev.location_details && <div>📍 {ev.location_details}</div>}
                  {getTargetLabel(ev) && <div>👥 {getTargetLabel(ev)}</div>}
                  {ev.description && <div style={{ color: '#6b7280', marginTop: '4px' }}>{ev.description}</div>}
                </div>

                {/* Edit button */}
                <button
                  className="btn-primary"
                  style={{ width: '100%', marginBottom: '12px' }}
                  onClick={() => { setSelectedEventCard(null); handleEditEvent(ev); }}
                >
                  ✏️ Edit Event
                </button>

                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px' }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                    Delete
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <button
                      onClick={() => { setSelectedEventCard(null); handleSeriesDelete('just_this', ev); }}
                      style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: 'white', textAlign: 'left', cursor: 'pointer' }}
                    >
                      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Just this event</div>
                      <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>Only removes this single occurrence</div>
                    </button>
                    {isPractice && (
                      <button
                        onClick={() => { setSelectedEventCard(null); handleSeriesDelete('this_and_future', ev); }}
                        style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: 'white', textAlign: 'left', cursor: 'pointer' }}
                      >
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>This and all future {dayName} practices</div>
                        <div style={{ color: '#6b7280', fontSize: '0.78rem' }}>Removes this and every "{ev.title}" from {dateLabel} onwards</div>
                      </button>
                    )}
                    {isPractice && (
                      <button
                        onClick={() => { setSelectedEventCard(null); handleSeriesDelete('all_series', ev); }}
                        style={{ padding: '10px 14px', borderRadius: '8px', border: '2px solid #fca5a5', background: '#fef2f2', textAlign: 'left', cursor: 'pointer' }}
                      >
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#dc2626' }}>Delete entire series</div>
                        <div style={{ color: '#9b1c1c', fontSize: '0.78rem' }}>Removes all "{ev.title}" practices on every {dayName}</div>
                      </button>
                    )}
                    {!isPractice && (
                      <button
                        onClick={() => { setSelectedEventCard(null); handleSeriesDelete('just_this', ev); }}
                        style={{ padding: '10px 14px', borderRadius: '8px', border: '2px solid #fca5a5', background: '#fef2f2', textAlign: 'left', cursor: 'pointer' }}
                      >
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#dc2626' }}>Delete this event</div>
                        <div style={{ color: '#9b1c1c', fontSize: '0.78rem' }}>This action cannot be undone</div>
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={() => setSelectedEventCard(null)} className="btn-secondary">Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Delete Scope Modal (Teams-style) */}
      {deleteConfirmEvent && (() => {
        const ev = deleteConfirmEvent;
        const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
        const dayName = dayNames[new Date(ev.start_datetime).getDay()];
        const isPractice = ev.event_type === 'practice';
        return (
          <div className="modal-overlay" onClick={() => setDeleteConfirmEvent(null)}>
            <div className="modal-content" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Delete Event</h2>
                <button onClick={() => setDeleteConfirmEvent(null)} className="btn-close">×</button>
              </div>
              <div className="modal-body">
                <p style={{ color: '#374151', fontWeight: 600, marginBottom: '4px' }}>{ev.title}</p>
                <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                  {new Date(ev.start_datetime).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <button onClick={() => handleSeriesDelete('just_this')} style={{
                    padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb',
                    background: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '0.925rem',
                  }}>
                    <div style={{ fontWeight: 600 }}>Just this event</div>
                    <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '2px' }}>Only removes this single occurrence</div>
                  </button>
                  {isPractice && (
                    <button onClick={() => handleSeriesDelete('this_and_future')} style={{
                      padding: '12px 16px', borderRadius: '8px', border: '1px solid #e5e7eb',
                      background: 'white', textAlign: 'left', cursor: 'pointer', fontSize: '0.925rem',
                    }}>
                      <div style={{ fontWeight: 600 }}>This and all following {dayName} practices</div>
                      <div style={{ color: '#6b7280', fontSize: '0.8rem', marginTop: '2px' }}>Removes this and future "{ev.title}" on {dayName}s</div>
                    </button>
                  )}
                  {isPractice && (
                    <button onClick={() => handleSeriesDelete('all_series')} style={{
                      padding: '12px 16px', borderRadius: '8px', border: '2px solid #fca5a5',
                      background: '#fef2f2', textAlign: 'left', cursor: 'pointer', fontSize: '0.925rem',
                    }}>
                      <div style={{ fontWeight: 600, color: '#dc2626' }}>Delete entire series</div>
                      <div style={{ color: '#9b1c1c', fontSize: '0.8rem', marginTop: '2px' }}>Removes all "{ev.title}" practices on every {dayName}</div>
                    </button>
                  )}
                  {!isPractice && (
                    <button onClick={() => handleSeriesDelete('just_this')} style={{
                      padding: '12px 16px', borderRadius: '8px', border: '2px solid #fca5a5',
                      background: '#fef2f2', textAlign: 'left', cursor: 'pointer', fontSize: '0.925rem',
                    }}>
                      <div style={{ fontWeight: 600, color: '#dc2626' }}>Confirm delete</div>
                      <div style={{ color: '#9b1c1c', fontSize: '0.8rem', marginTop: '2px' }}>This action cannot be undone</div>
                    </button>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={() => setDeleteConfirmEvent(null)} className="btn-secondary">Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Bulk Delete Practices Modal */}
      {showBulkDeleteModal && (
        <div className="modal-overlay" onClick={() => setShowBulkDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🗑️ Delete Practices</h2>
              <button onClick={() => setShowBulkDeleteModal(false)} className="btn-close">×</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#6b7280', marginBottom: '1rem', fontSize: '0.95rem' }}>
                Search for practices by title and/or date range, then select which ones to remove.
              </p>
              <div className="form-group">
                <label>Practice Title (partial match)</label>
                <input type="text" value={bulkDeleteFilter.title}
                  onChange={(e) => setBulkDeleteFilter({ ...bulkDeleteFilter, title: e.target.value })}
                  placeholder="e.g., Level 3 Practice" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>From Date</label>
                  <input type="date" value={bulkDeleteFilter.start_date}
                    onChange={(e) => setBulkDeleteFilter({ ...bulkDeleteFilter, start_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>To Date</label>
                  <input type="date" value={bulkDeleteFilter.end_date}
                    onChange={(e) => setBulkDeleteFilter({ ...bulkDeleteFilter, end_date: e.target.value })} />
                </div>
              </div>
              <button onClick={handleBulkDeleteSearch} className="btn-primary" style={{ marginBottom: '1rem' }}>
                🔍 Search Practices
              </button>

              {bulkDeleteSearched && (
                <>
                  {bulkDeleteMatches.length === 0 ? (
                    <p style={{ color: '#6b7280' }}>No practices found matching your search.</p>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 600 }}>{bulkDeleteMatches.length} practice{bulkDeleteMatches.length !== 1 ? 's' : ''} found</span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button type="button" className="btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                            onClick={() => setBulkDeleteSelected(bulkDeleteMatches.map(e => e.id))}>
                            Select All
                          </button>
                          <button type="button" className="btn-secondary" style={{ padding: '4px 10px', fontSize: '0.8rem' }}
                            onClick={() => setBulkDeleteSelected([])}>
                            Clear
                          </button>
                        </div>
                      </div>
                      <div style={{ maxHeight: '280px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                        {bulkDeleteMatches.map(event => {
                          const isSelected = bulkDeleteSelected.includes(event.id);
                          const dt = new Date(event.start_datetime);
                          const label = dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
                          const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                          return (
                            <label key={event.id} style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '10px 14px', cursor: 'pointer',
                              background: isSelected ? '#fef2f2' : 'white',
                              borderBottom: '1px solid #f3f4f6',
                            }}>
                              <input type="checkbox" checked={isSelected}
                                onChange={(e) => {
                                  setBulkDeleteSelected(e.target.checked
                                    ? [...bulkDeleteSelected, event.id]
                                    : bulkDeleteSelected.filter(id => id !== event.id));
                                }} />
                              <div>
                                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{event.title}</div>
                                <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>{label} at {time}</div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                      {bulkDeleteSelected.length > 0 && (
                        <div style={{ marginTop: '10px', padding: '10px 14px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fca5a5', color: '#dc2626', fontWeight: 600 }}>
                          ⚠️ {bulkDeleteSelected.length} practice{bulkDeleteSelected.length !== 1 ? 's' : ''} selected for deletion
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowBulkDeleteModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleBulkDeleteConfirm} className="btn-primary"
                disabled={bulkDeleteSelected.length === 0}
                style={{ background: bulkDeleteSelected.length > 0 ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : '#9ca3af', cursor: bulkDeleteSelected.length > 0 ? 'pointer' : 'not-allowed' }}>
                Delete {bulkDeleteSelected.length > 0 ? `${bulkDeleteSelected.length} ` : ''}Practice{bulkDeleteSelected.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Meet Detail Modal */}
      {showMeetDetailModal && selectedMeetDetail && (
        <div className="modal-overlay" onClick={() => setShowMeetDetailModal(false)}>
          <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedMeetDetail.event_name}</h2>
              <button onClick={() => setShowMeetDetailModal(false)} className="btn-close">×</button>
            </div>
            <div className="modal-body">
              <div className="meet-detail-grid">
                <div>
                  <strong>Type:</strong>{' '}
                  <span className="event-type-badge">{selectedMeetDetail.event_type}</span>
                </div>
                <div>
                  <strong>Date:</strong> {new Date(selectedMeetDetail.event_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })}
                </div>
                {selectedMeetDetail.registration_deadline && (() => {
                  const ds = getDeadlineStatus(selectedMeetDetail.registration_deadline);
                  return (
                    <div>
                      <strong>Registration Deadline:</strong>{' '}
                      {new Date(selectedMeetDetail.registration_deadline).toLocaleDateString('en-US', { timeZone: 'UTC' })}{' '}
                      {ds && <span style={{ color: ds.color, fontWeight: 600 }}>— {ds.label}</span>}
                    </div>
                  );
                })()}
                {selectedMeetDetail.location_details && (
                  <div>
                    <strong>Location:</strong>{' '}
                    <a
                      href={`https://maps.google.com/?q=${encodeURIComponent(selectedMeetDetail.location_details)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      📍 {selectedMeetDetail.location_details}
                    </a>
                  </div>
                )}
                {selectedMeetDetail.cost_per_athlete && (
                  <div><strong>Cost per athlete:</strong> ${parseFloat(selectedMeetDetail.cost_per_athlete).toFixed(2)}</div>
                )}
                {selectedMeetDetail.requires_usag_number ? (
                  <div><strong>USAG #:</strong> <span style={{ color: '#dc2626' }}>Required</span></div>
                ) : null}
                {selectedMeetDetail.levels_allowed && (
                  <div><strong>Levels:</strong> {
                    (() => {
                      try {
                        const lvls = typeof selectedMeetDetail.levels_allowed === 'string'
                          ? JSON.parse(selectedMeetDetail.levels_allowed)
                          : selectedMeetDetail.levels_allowed;
                        return Array.isArray(lvls) ? lvls.join(', ') : lvls;
                      } catch { return selectedMeetDetail.levels_allowed; }
                    })()
                  }</div>
                )}
                {selectedMeetDetail.max_participants && (
                  <div><strong>Capacity:</strong> {selectedMeetDetail.current_registrations || 0} / {selectedMeetDetail.max_participants} registered</div>
                )}
                {selectedMeetDetail.description && (
                  <div style={{ gridColumn: '1 / -1' }}><strong>Notes:</strong><p>{selectedMeetDetail.description}</p></div>
                )}
              </div>

              {/* Packing Checklist */}
              <div className="checklist-section">
                <h3>📦 Packing Checklist</h3>
                {meetChecklist.length === 0 && <p style={{ color: '#6b7280' }}>No checklist items yet.</p>}
                <ul className="checklist-list">
                  {meetChecklist.map(item => (
                    <li key={item.id} className={`checklist-item${item.completed ? ' completed' : ''}`}>
                      <label>
                        <input
                          type="checkbox"
                          checked={!!item.completed}
                          onChange={() => handleToggleChecklistItem(item)}
                        />
                        <span>{item.item_name}</span>
                        {item.description && <span className="checklist-desc"> — {item.description}</span>}
                      </label>
                      {user && (user.role === 'admin' || user.role === 'coach') && (
                        <button className="btn-icon delete" onClick={() => handleDeleteChecklistItem(item.id)}>🗑️</button>
                      )}
                    </li>
                  ))}
                </ul>
                {user && (user.role === 'admin' || user.role === 'coach') && (
                  <div className="checklist-add">
                    <input
                      type="text"
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddChecklistItem()}
                      placeholder="Add item (e.g. Grips, Leos, Water bottle)"
                    />
                    <button onClick={handleAddChecklistItem} className="btn-primary">Add</button>
                  </div>
                )}
              </div>
            </div>
            {isAdminOrCoach && (
              <div className="modal-footer">
                <button
                  className="btn-secondary"
                  onClick={() => {
                    setShowMeetDetailModal(false);
                    navigate('/meet-coordination', { state: { meetId: selectedMeetDetail.id } });
                  }}
                >
                  ⚙️ Team Selection & Results →
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Practice Schedule Modal */}
      {showBulkModal && (
        <div className="modal-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Create Practice Schedule</h2>
              <button onClick={() => setShowBulkModal(false)} className="btn-close">×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Practice Title *</label>
                <input type="text" value={bulkEvent.title}
                  onChange={(e) => setBulkEvent({ ...bulkEvent, title: e.target.value })}
                  placeholder="e.g., Team Practice, Level 3 Practice" />
              </div>

              <div className="form-group">
                <label>Recurring Days *</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                  {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(day => {
                    const selected = bulkEvent.days_of_week.includes(day);
                    return (
                      <button key={day} type="button"
                        onClick={() => {
                          const days = selected
                            ? bulkEvent.days_of_week.filter(d => d !== day)
                            : [...bulkEvent.days_of_week, day];
                          setBulkEvent({ ...bulkEvent, days_of_week: days });
                        }}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '8px',
                          border: selected ? '2px solid #10b981' : '2px solid #e5e7eb',
                          background: selected ? '#10b981' : 'white',
                          color: selected ? 'white' : '#374151',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        {day.slice(0, 3)}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Time *</label>
                  <input type="time" value={bulkEvent.start_time}
                    onChange={(e) => setBulkEvent({ ...bulkEvent, start_time: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>End Time *</label>
                  <input type="time" value={bulkEvent.end_time}
                    onChange={(e) => setBulkEvent({ ...bulkEvent, end_time: e.target.value })} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input type="date" value={bulkEvent.start_date}
                    onChange={(e) => setBulkEvent({ ...bulkEvent, start_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>End Date *</label>
                  <input type="date" value={bulkEvent.end_date}
                    onChange={(e) => setBulkEvent({ ...bulkEvent, end_date: e.target.value })} />
                </div>
              </div>

              {isAdminOrCoach && (
                <div className="form-group">
                  <label>Assign to</label>
                  <div className="assign-to-options">
                    <label className="assign-radio">
                      <input type="radio" name="bulkAssignTo" value="all"
                        checked={bulkEvent.assignTo === 'all'}
                        onChange={() => setBulkEvent({ ...bulkEvent, assignTo: 'all', assignClassId: '', assignAthleteIds: [] })} />
                      🏫 All gymnasts
                    </label>
                    <label className="assign-radio">
                      <input type="radio" name="bulkAssignTo" value="class"
                        checked={bulkEvent.assignTo === 'class'}
                        onChange={() => setBulkEvent({ ...bulkEvent, assignTo: 'class', assignAthleteIds: [] })} />
                      📋 Specific class / level
                    </label>
                    <label className="assign-radio">
                      <input type="radio" name="bulkAssignTo" value="athlete"
                        checked={bulkEvent.assignTo === 'athlete'}
                        onChange={() => setBulkEvent({ ...bulkEvent, assignTo: 'athlete', assignClassId: '' })} />
                      👤 Individual athlete(s)
                    </label>
                  </div>
                  {bulkEvent.assignTo === 'class' && (
                    <select className="assign-select"
                      value={bulkEvent.assignClassId}
                      onChange={(e) => setBulkEvent({ ...bulkEvent, assignClassId: e.target.value })}>
                      <option value="">Select class...</option>
                      {allClasses.map(cls => (
                        <option key={cls.id} value={cls.id}>{cls.class_name}{cls.level ? ` — ${cls.level}` : ''}</option>
                      ))}
                    </select>
                  )}
                  {bulkEvent.assignTo === 'athlete' && (
                    <div className="athlete-multi-select">
                      <div className="athlete-select-label">Select athletes (check all that apply):</div>
                      <div className="athlete-checkboxes">
                        {allAthletes.map(athlete => (
                          <label key={athlete.id} className="athlete-checkbox">
                            <input type="checkbox"
                              checked={bulkEvent.assignAthleteIds.includes(String(athlete.id))}
                              onChange={(e) => {
                                const ids = e.target.checked
                                  ? [...bulkEvent.assignAthleteIds, String(athlete.id)]
                                  : bulkEvent.assignAthleteIds.filter(id => id !== String(athlete.id));
                                setBulkEvent({ ...bulkEvent, assignAthleteIds: ids });
                              }} />
                            {athlete.name}
                          </label>
                        ))}
                      </div>
                      {bulkEvent.assignAthleteIds.length > 0 && (
                        <div className="selected-count">
                          {bulkEvent.assignAthleteIds.length} athlete{bulkEvent.assignAthleteIds.length > 1 ? 's' : ''} selected
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Location & Travel Details</label>
                <textarea value={bulkEvent.location_details} rows="3"
                  onChange={(e) => setBulkEvent({ ...bulkEvent, location_details: e.target.value })}
                  placeholder="Address, venue name, parking info, etc." />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea value={bulkEvent.description} rows="3"
                  onChange={(e) => setBulkEvent({ ...bulkEvent, description: e.target.value })}
                  placeholder="Optional notes for all practices" />
              </div>

              {bulkEvent.start_date && bulkEvent.end_date && bulkEvent.days_of_week.length > 0 && (() => {
                let count = 0;
                const dayIndexMap = { Sunday:0, Monday:1, Tuesday:2, Wednesday:3, Thursday:4, Friday:5, Saturday:6 };
                const targetDays = new Set(bulkEvent.days_of_week.map(d => dayIndexMap[d]));
                const cur = new Date(bulkEvent.start_date + 'T00:00:00');
                const last = new Date(bulkEvent.end_date + 'T00:00:00');
                while (cur <= last) { if (targetDays.has(cur.getDay())) count++; cur.setDate(cur.getDate() + 1); }
                return (
                  <div style={{ background: '#f0fdf4', padding: '12px', borderRadius: '8px', border: '1px solid #86efac' }}>
                    <p style={{ color: '#16a34a', margin: 0, fontWeight: 600 }}>
                      ✓ This will create <strong>{count}</strong> practice{count !== 1 ? 's' : ''}
                    </p>
                    <p style={{ color: '#15803d', margin: '4px 0 0 0', fontSize: '0.875rem' }}>
                      {bulkEvent.days_of_week.join(', ')} • {bulkEvent.start_time} - {bulkEvent.end_time}
                    </p>
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowBulkModal(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleBulkCreate} className="btn-primary">Create All Practices</button>
            </div>
          </div>
        </div>
      )}

      {/* Layered Practice Plan (rotations, workout, S&C, theme) */}
      {practicePlan && (
        <PracticePlanModal
          classId={practicePlan.classId}
          calendarEventId={practicePlan.calendarEventId}
          practiceDate={practicePlan.date}
          className={practicePlan.className}
          athletes={allAthletes}
          onClose={() => { setPracticePlan(null); fetchPlanSummary(); }}
        />
      )}

    </div>
  );
}

export default TeamCalendar;